import AgoraRTC, {
    CameraVideoTrackInitConfig, ClientRoleOptions,
    IAgoraRTCClient, ICameraVideoTrack,
    ILocalAudioTrack, ILocalTrack,
    ILocalVideoTrack, IMicrophoneAudioTrack, MicrophoneAudioTrackInitConfig,
    SDK_MODE,
    UID
} from "agora-rtc-sdk-ng"
import {WebConferenceTypes} from "@share/web-conferences/enums/WebConferenceTypes";
import {AgoraCodecs} from "@share/web-conferences/enums/AgoraCodecs";
import {AgoraRemoteUsersInterface} from "@share/web-conferences/interfaces/AgoraRemoteUsersInterface";
import {AgoraInterface} from "@share/web-conferences/interfaces/AgoraInterface";
import {AgoraCredentials} from "@api/models/web-conferences/AgoraCredentials";
import {TracksManagerInterface} from "@share/web-conferences/interfaces/TracksManagerInterface";
import {UserRoleType} from "@share/web-conferences/enums/UserRoleType";
import {
    lowStreamParams,
    retryConfig,
    videoLowQualityEncoding, videoTopQualityEncoding
} from "@share/web-conferences/agora/defaults";
import {AgoraVolumesManagerInterface} from "@share/web-conferences/interfaces/AgoraVolumesManagerInterface";
import {AgoraVolumesManager} from "@share/web-conferences/agora/AgoraVolumesManager";
import {getClientRole, isPresenter} from "@share/web-conferences/helpers/UserRoleHelper";
import {defineMode} from "@share/web-conferences/helpers/AgoraClientMode";
import Vue from 'vue';
import {AgoraRoleTypes} from "@share/web-conferences/enums/AgoraRoleTypes";
import {AgoraClientModes} from "@share/web-conferences/enums/AgoraClientModes";
import {DevicesInfo} from "@share/web-conferences/types/DevicesInfo";
import {getDevicesFromStorage} from "@share/web-conferences/helpers/LocalStorageHelper";
import {DeviceStatesPromisesQueue} from "@share/web-conferences/helpers/DeviceStatesPromisesQueue";
import { createAudioTrack, createVideoTrack } from "../helpers/TracksHelper";
import { AgoraNetworkQualityInterface } from "../interfaces/AgoraQualityCheckerInterface";
import {AgoraVideoEncoding} from "@share/web-conferences/enums/AgoraVideoEncoding";
import {StoreHelper} from "@share/web-conferences/helpers/StoreHelper";
import {isSharingGetter} from "@share/web-conferences/store/getters";
import { AgoraVirtualBackground } from "./AgoraVirtualBackground";

export class Agora implements AgoraInterface {
    client: IAgoraRTCClient;
    clientMode: SDK_MODE;
    uid: UID;
    joined = false;
    micIsOn = true;
    videoIsOn = true;
    videoDeviceId?: string = undefined;
    audioDeviceId?: string = undefined;
    tracksStatesQueues: Record<"video" | "audio", DeviceStatesPromisesQueue> = {
        "video": new DeviceStatesPromisesQueue(this.switchVideoTrack.bind(this), 500),
        "audio": new DeviceStatesPromisesQueue(this.switchAudioTrack.bind(this))
    };

    private agoraVirtualBackground: AgoraVirtualBackground;

    constructor(
        public remoteUsers: AgoraRemoteUsersInterface,
        public tracksManager: TracksManagerInterface,
        public volumesManager: AgoraVolumesManagerInterface,
        public networkQuality: AgoraNetworkQualityInterface,
        type: WebConferenceTypes,
        private readonly storeHelper: StoreHelper
    ) {
        this.clientMode = defineMode(type);
        this.client = Vue.observable(AgoraRTC.createClient({
            mode: this.clientMode,
            codec: AgoraCodecs.Vp8,
            httpRetryConfig: retryConfig,
            websocketRetryConfig: retryConfig
        }));

        this.setDevicesData(getDevicesFromStorage());

        this.setDualStreamMode();
    }

    /**
     * Enable dual stream mode
     *
     */
    async setDualStreamMode(): Promise<void> {
        this.client.setLowStreamParameter(lowStreamParams);
        try {
            await this.client.enableDualStream();
        }
        catch (e) {
            if (e.code == 'NOT_SUPPORTED') {
                await this.client.disableDualStream();
            }
        }
    }

    /**
     * Set client role if user is presenter then host else audience
     * @param userRole
     */
    async setRole(userRole: UserRoleType): Promise<void> {
        if (this.clientMode == "rtc") {return;}
        await this.setClientRole(getClientRole(userRole));
    }

    async join(userRole: UserRoleType, credentials: AgoraCredentials): Promise<boolean> {
        try {
            console.log('%cjoining', 'font-size:30px');
            await this.setRole(userRole);

            this.uid = await this.client.join(
                credentials.appId, credentials.channelId, credentials.token, credentials.userId
            );

            await this.remoteUsers.subscribe(this.client, this.tracksManager);
            await this.volumesManager.start(this.client);
            await this.networkQuality.subscribe(this.client);

            this.joined = true;

            return true;
        }
        catch (e) {
            console.log(e);
            this.storeHelper.store.dispatch('reportError', e);
        }

        return false;
    }

    async renewToken(newToken: string): Promise<boolean> {
        let result = true;
        try {
            await this.client.renewToken(newToken as string);
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
            result = false;
        }
        return result;
    }

    async leave(): Promise<void> {
        if (this.joined) {
            console.log('%cleaving', 'font-size: 30px');
            try {
                await this.tracksManager.release();
            } catch (e) {
                this.storeHelper.store.dispatch('reportException', e);
            }
            await this.client.leave();

            this.joined = false;
        }
    }

    /**
     * Create local audio track
     * @private
     */
    async createAudioTrack(config: MicrophoneAudioTrackInitConfig = {}): Promise<ILocalAudioTrack | null> {
        return createAudioTrack(config, this.audioDeviceId)
    }

    /**
     * Create local video track
     * @private
     */
    async createVideoTrack(config: CameraVideoTrackInitConfig = {}): Promise<ILocalVideoTrack | null> {
        return createVideoTrack(config, this.videoDeviceId);
    }

    /**
     * Creates and publish local tracks
     * @private
     */
    async createLocalTracks(): Promise<void> {
        try {
            let [localAudioTrack, localVideoTrack] = await Promise.all([
                this.micIsOn ? this.createAudioTrack() : null,
                this.videoIsOn ? this.createVideoTrack({
                    encoderConfig: this.storeHelper.store.getters[isSharingGetter](this.storeHelper.stateKey)
                                    ? videoLowQualityEncoding
                                    : videoTopQualityEncoding
                }) : null
            ]);

            await this.publishLocalTracks([localAudioTrack as ILocalTrack, localVideoTrack as ILocalTrack]);

            await this.tracksManager.add({
                uid: this.uid,
                audio: localAudioTrack ?? undefined,
                video: localVideoTrack ?? undefined,
                isLocal: true
            });
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
        }
    }

    /**
     * Publish local tracks
     * @param tracks
     * @private
     */
    async publishLocalTracks(tracks: ILocalTrack[] = []): Promise<void> {
        try {
            const toPublish = tracks.filter(item => item);
            toPublish && toPublish.length && await this.client.publish(toPublish);
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
        }
    }

    /**
     * Start indicating users volumes
     */
    async initVolumesManager(): Promise<void> {
        this.volumesManager = new AgoraVolumesManager();
        await this.volumesManager.start(this.client);
    }

    async changeRoleToAudience() {
        try {
            if (this.clientMode == AgoraClientModes.Rtc) {
                await this.leave();
                return;
            }
            //need to remove track from background effects
            this.agoraVirtualBackground?.resetTrack(null);
            await this.client.unpublish(this.client.localTracks);
            await this.setClientRole(AgoraRoleTypes.Audience);
            await this.tracksManager.removeLocal();
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
        }
    }

    async changeRoleToHost(createTracks = true) {
        try {
            await this.setClientRole(AgoraRoleTypes.Host);
            createTracks && await this.createLocalTracks();
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
        }
    }

    async changeAudioDevice(deviceId: string): Promise<void> {
        const userTracks = this.tracksManager.getLocalUserTracks();
        if (userTracks && userTracks.audio) {
            await (userTracks.audio as IMicrophoneAudioTrack).setDevice(deviceId);
        }
    }

    async changeVideoDevice(deviceId: string): Promise<void> {
        const userTracks = this.tracksManager.getLocalUserTracks();
        if (userTracks && userTracks.video) {
            await (userTracks.video as ICameraVideoTrack).setDevice(deviceId);
        }
    }

    setDevicesData(data: DevicesInfo): void {
        Object.assign(this, data);
        this.tracksStatesQueues.audio.previousState = this.micIsOn;
        this.tracksStatesQueues.video.previousState = this.videoIsOn;
    }

    clean(removeListeners = true): void {
        this.client?.leave();
        removeListeners && this.client?.removeAllListeners();
        this.tracksManager.clean();
        this.remoteUsers.clean();
        this.volumesManager.clean();
        this.joined = false;
        this.networkQuality.unsubscribe();
        // this.statistics.clean();
    }

    async switchVideoTrack(state: boolean) {
        const localTracks = this.tracksManager.getLocalUserTracks();

        if (localTracks.video) {
            await (localTracks.video as ILocalVideoTrack).setEnabled(state);
        } else if (state) {
            localTracks.video = await this.createVideoTrack() as ILocalVideoTrack;
            localTracks.video && await this.publishLocalTracks([localTracks.video as ILocalTrack]);
        }
    }

    async switchAudioTrack(state: boolean) {
        const localTracks = this.tracksManager.getLocalUserTracks();

        if (localTracks.audio) {
            await (localTracks.audio as ILocalAudioTrack).setEnabled(state);
        } else if (state) {
            localTracks.audio = await this.createAudioTrack() as ILocalAudioTrack;
            localTracks.audio && await this.publishLocalTracks([localTracks.audio as ILocalTrack]);
        }
    }

    async switchMic(state: boolean): Promise<void> {
        try {
            this.tracksManager.changeAudioState(this.uid, state);
            this.micIsOn = state;
            this.tracksStatesQueues.audio.add(state);
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
        }
    }

    async switchVideo(state: boolean): Promise<void> {
        try {
            this.tracksManager.changeVideoState(this.uid, state);
            this.videoIsOn = state;
            await this.tracksStatesQueues.video.add(state);
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
        }
    }

    async lowerQuality() {
        await this.changeEncoderConfiguration(videoLowQualityEncoding);
    }

    async raiseQuality() {
        await this.changeEncoderConfiguration(videoTopQualityEncoding);
    }

    setAgoraVirtualBackground(agoraVirtualBackground: AgoraVirtualBackground) {
        this.agoraVirtualBackground = agoraVirtualBackground;
    }

    private async changeEncoderConfiguration(encoding: AgoraVideoEncoding) {
        const localTracks = this.tracksManager.getLocalUserTracks();

        if (localTracks && localTracks.video) {
            try {
                await (localTracks.video as ICameraVideoTrack).setEncoderConfiguration(encoding);
            }
            catch (e) {
                this.storeHelper.store.dispatch('reportException', e);
            }
        }
    }

    private async setClientRole(role: AgoraRoleTypes): Promise<void> {
        // level 1 - Low latency, ILS Standard, low price - for Audience
        // level 2 - Ultra low latency, ILS Premium, high price - for Speakers
        await this.client.setClientRole(role, role == AgoraRoleTypes.Audience ? { level: 1 } : undefined);
    }
}
